package com.github.jangalinski.tabweb._app

import androidx.compose.runtime.Composable
import com.github.jangalinski.tabweb._foundation.compose.KDiv
import com.github.jangalinski.tabweb._foundation.compose.KButton
import com.github.jangalinski.tabweb._foundation.compose.KSpan
import com.github.jangalinski.tabweb.navigation.TabwebSectionNavigation
import com.github.jangalinski.tabweb.navigation.pageMeta
import com.github.jangalinski.tabweb._foundation.css.ClassNames
import com.github.jangalinski.tabweb._foundation.css.ClassNames.modifier
import com.github.jangalinski.tabweb.navigation.TabwebNavbar
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.dataAttr
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.remove

/**
 * Kobweb layout that renders the shared Tabweb page shell around a route.
 *
 * The composition follows the preview's major regions: `BEGIN SIDEBAR` or
 * `BEGIN NAVBAR`, then `page-wrapper`, `BEGIN PAGE HEADER`, `BEGIN PAGE BODY`,
 * and `BEGIN FOOTER`. The route itself is supplied through [content], while
 * shared navigation and page metadata arrive through the configured static
 * navigation tree. Route-specific overrides remain available through the
 * explicit overload below.
 *
 * Keeping this shell in a Kobweb `@Layout` lets pages focus on their own
 * content while preserving one stable DOM boundary for Tabweb CSS and
 * responsive behavior.
 *
 * ```
 * TabwebPage
 *  ├─ TabwebSidebar and TabwebNavbar
 *  ├─ TabwebPageWrapper
 *  │  ├─ TabwebPageHeader
 *  │  ├─ PagePart (TabwebPageBody)
 *  │  └─ TabwebFooter
 * ```
 *
 * @param ctx Kobweb page context containing route-scoped layout data.
 * @param content route content rendered in the page body.
 */
@Layout
@Composable
fun TabwebLayout(
  ctx: PageContext,
  content: @Composable () -> Unit
) {
  val site = LocalTabwebSiteConfig.current
  val activeRoute = BasePath.remove(ctx.route.path)
  val pageMeta = site.shell.navigation?.pageMeta(activeRoute)
    ?: TabwebPageMeta(title = activeRoute)

  TabwebLayout(
    ctx = ctx,
    layoutData = TabwebLayoutData(activeRoute = activeRoute),
    pageMeta = pageMeta,
    content = content,
  )
}

/**
 * Renders the shared Tabweb page shell with explicitly supplied route data.
 *
 * This overload is useful for generated page sources, such as Kobweb Markdown,
 * whose layout data is available to the layout but cannot be added to Kobweb's
 * read-only page data store during composition.
 *
 * @param ctx Kobweb page context for the current route.
 * @param layoutData route-specific navigation and footer data.
 * @param pageMeta title, subtitle, and breadcrumbs for the page header.
 * @param content route content rendered in the page body.
 */
@Composable
fun TabwebLayout(
  ctx: PageContext,
  layoutData: TabwebLayoutData,
  pageMeta: TabwebPageMeta,
  content: @Composable () -> Unit,
) {
  val site = LocalTabwebSiteConfig.current
  val footer = layoutData.footer ?: site.shell.footer

  TabwebPage {

    TabwebNavbar(
      brand = site.shell.brand,
      data = site.shell.navbar.create(layoutData.activeRoute),
      actions = site.shell.navbarActions,
    )

    TabwebPageWrapper {

      TabwebPageHeader(
        title = pageMeta.title,
        subtitle = pageMeta.subtitle,
        breadcrumbs = pageMeta.breadcrumbs,
      )

      TabwebPageBody {
        val sectionNavigation = layoutData.sectionNavigation
          ?.create(layoutData.activeRoute)

        if (sectionNavigation == null) {
          content()
        } else {
          KDiv(modifier = Modifier.classNames("row", "g-0")) {
            KDiv(modifier = Modifier.classNames("col-lg-3", "pe-lg-4", "mb-4", "mb-lg-0")) {
              KButton(
                modifier = Modifier.classNames("btn", "btn-outline-secondary", "d-lg-none", "mb-3")
                  .dataAttr("bs-toggle", "offcanvas")
                  .dataAttr("bs-target", "#section-navigation-offcanvas")
                  .attr("aria-controls", "section-navigation-offcanvas"),
              ) {
                KSpan(text = "Open documentation menu")
              }
              KDiv(
                modifier = Modifier.classNames("offcanvas-lg", "offcanvas-start")
                  .attr("id", "section-navigation-offcanvas")
                  .attr("aria-label", "Documentation"),
              ) {
                KDiv(modifier = Modifier.classNames("offcanvas-header", "d-lg-none")) {
                  KSpan(modifier = Modifier.classNames("offcanvas-title"), text = "Documentation")
                  KButton(
                    modifier = Modifier.classNames("btn-close")
                      .dataAttr("bs-dismiss", "offcanvas")
                      .dataAttr("bs-target", "#section-navigation-offcanvas")
                      .attr("aria-label", "Close documentation menu"),
                  ) {}
                }
                KDiv(modifier = Modifier.classNames("offcanvas-body")) {
                  TabwebSectionNavigation(sectionNavigation)
                }
              }
            }
            KDiv(modifier = Modifier.classNames("col-lg-9")) {
              content()
            }
          }
        }
      }

      footer(Modifier.classNames("footer", "footer-transparent", "d-print-none"))
    }
  }
}

/**
 * Root container for a Tabweb page shell.
 *
 * This corresponds to the preview's outer `<!-- BEGIN PAGE -->` region and
 * normally contains the sidebar, navbar, and [TabwebPageWrapper]. In Kobweb
 * terms, it is the stable composable boundary around route content rather
 * than a page route itself.
 */
@Composable
private fun TabwebPage(content: @Composable () -> Unit) {
  KDiv(modifier = ClassNames.page.modifier(), content = content)
}

/**
 * Wrapper for the route-facing portion of a Tabweb page.
 *
 * This is the preview's `.page-wrapper` block. It groups the page header,
 * [TabwebPageBody], and footer while the outer [TabwebPage] keeps global
 * navigation beside it. That separation mirrors Kobweb's layout design:
 * shared chrome surrounds the composable content supplied by a route.
 */
@Composable
private fun TabwebPageWrapper(content: @Composable () -> Unit) {
  KDiv(modifier = ClassNames.pageWrapper.modifier(), content = content)
}
